import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Circle, Target } from 'lucide-react';

interface TimelineItem {
  week: string;
  action: string;
  detail: string;
}

interface TimelineChartProps {
  items: TimelineItem[];
  partnerPrimaryColor?: string;
  milestoneTitle?: string;
  milestoneDescription?: string;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({ 
  items, 
  partnerPrimaryColor,
  milestoneTitle = "Success Milestone Achieved",
  milestoneDescription = "Ready to scale and make global impact"
}) => {
  const primaryColor = partnerPrimaryColor || '#B100FF';

  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="w-full max-w-7xl">
        {/* Timeline container */}
        <div className="relative">
          {/* Main timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-primary to-primary/50" 
               style={{ 
                 background: `linear-gradient(to bottom, ${primaryColor}, ${primaryColor}80)` 
               }} 
          />

          {/* Timeline items */}
          <div className="space-y-8">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const delay = index * 0.15;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay }}
                  className="relative flex items-start gap-6 group"
                >
                  {/* Timeline dot/icon */}
                  <div className="relative z-10 flex-shrink-0">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: delay + 0.2, type: "spring" }}
                      className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl border-4 border-background"
                      style={{ 
                        backgroundColor: isLast ? primaryColor : '#ffffff',
                        borderColor: isLast ? 'transparent' : primaryColor,
                      }}
                    >
                      {isLast ? (
                        <Target className="w-8 h-8 text-white" />
                      ) : (
                        <span className="text-2xl font-bold" 
                              style={{ color: primaryColor }}>
                          {index + 1}
                        </span>
                      )}
                    </motion.div>
                  </div>

                  {/* Content card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: delay + 0.3 }}
                    className={`
                      flex-1 p-6 rounded-2xl border-2 
                      bg-white shadow-lg
                      group-hover:shadow-2xl group-hover:-translate-y-1
                      transition-all duration-300
                      ${isLast ? 'border-primary ring-4 ring-primary/20' : 'border-border'}
                    `}
                    style={isLast ? { 
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 4px ${primaryColor}20`
                    } : {}}
                  >
                    {/* Week badge */}
                    <div 
                      className="inline-block px-4 py-1.5 rounded-full text-sm font-bold mb-3"
                      style={{
                        backgroundColor: `${primaryColor}15`,
                        color: primaryColor,
                      }}
                    >
                      {item.week}
                    </div>

                    {/* Action */}
                    <h3 
                      className="text-2xl font-bold mb-2"
                      style={{ color: isLast ? primaryColor : '#1a1a1a' }}
                    >
                      {item.action}
                    </h3>

                    {/* Detail */}
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {item.detail}
                    </p>

                    {/* Progress indicator for last item */}
                    {isLast && (
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ duration: 1.5, delay: delay + 0.8 }}
                        className="mt-4 h-2 rounded-full overflow-hidden"
                        style={{ backgroundColor: `${primaryColor}20` }}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 1.5, delay: delay + 1 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: primaryColor }}
                        />
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Success indicator at the end */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: items.length * 0.15 + 0.5,
              type: "spring",
              bounce: 0.5
            }}
            className="mt-8 ml-8 flex items-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-primary/10 to-transparent border-l-4"
            style={{ borderColor: primaryColor }}
          >
            <CheckCircle2 className="w-10 h-10" style={{ color: primaryColor }} />
            <div>
              <h4 className="text-xl font-bold text-foreground">{milestoneTitle}</h4>
              <p className="text-muted-foreground">{milestoneDescription}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

